import React, { useEffect } from 'react';
import { Link, LockOutlinedIcon, Text } from '@billda/design-system';
import { useMagicCodeLoginStore } from '../../stores/magicCodeLoginStore';
import EmailStep from './EmailStep';
import CodeStep from './CodeStep';
import { useSearchParams } from 'react-router-dom';

const LinkedInIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const InstagramIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
  </svg>
);

const FacebookIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const TikTokIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

const socialLinks = [
  {
    href: 'https://www.linkedin.com/company/billdaltd/',
    label: 'LinkedIn',
    Icon: LinkedInIcon,
  },
  {
    href: 'https://www.instagram.com/billdaltd?igsh=Y3htZWkzMG10cHpm&utm_source=qr',
    label: 'Instagram',
    Icon: InstagramIcon,
  },
  {
    href: 'https://www.facebook.com/share/17HmpUBwAC/?mibextid=wwXIfr',
    label: 'Facebook',
    Icon: FacebookIcon,
  },
  {
    href: 'https://www.tiktok.com/@billdaltd?_r=1&_t=ZN-946ZSWn2qz0',
    label: 'TikTok',
    Icon: TikTokIcon,
  },
];

interface MagicCodeLoginFormProps {
  /** Called when login is successful */
  onLoginSuccess?: () => void;
}

/**
 * A clean, stepped magic code login form with email entry and code verification
 */

export const MagicCodeLoginForm: React.FC<MagicCodeLoginFormProps> = ({
  onLoginSuccess,
}) => {
  const { email, currentStep, setEmail, setCode, goToCodeStep } =
    useMagicCodeLoginStore();
  const [searchParams] = useSearchParams();
  const [shouldAutoSubmit, setShouldAutoSubmit] = React.useState(false);

  useEffect(() => {
    const urlEmail = searchParams.get('email');
    const urlCode = searchParams.get('code');

    if (urlEmail) {
      setEmail(urlEmail);
      goToCodeStep();

      if (urlCode) {
        setCode(urlCode);
        // Set flag to auto-submit once the CodeStep is rendered
        setShouldAutoSubmit(true);
      }
    }
  }, [searchParams, setEmail, setCode, goToCodeStep]);

  const handleEmailStepSuccess = () => {
    // Email step handles the store state transition
    // This callback is for any additional logic if needed
  };

  const handleCodeStepSuccess = () => {
    // Code step handles the store reset
    setShouldAutoSubmit(false); // Reset auto-submit flag
    if (onLoginSuccess) {
      onLoginSuccess();
    }
  };

  const handleBackToEmail = () => {
    // CodeStep handles the store state transition
    // This callback is for any additional logic if needed
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-8">
        <Text
          as="h1"
          variant="h4"
          className="mb-2 font-bold text-content-primary"
        >
          {currentStep === 'email' ? 'Welcome back.' : 'Check your email.'}
        </Text>
        <Text
          variant="body1"
          color="secondary"
          data-testid="magic-code-instruction"
        >
          {currentStep === 'email'
            ? "Enter your email and we'll send you a secure sign-in code."
            : `We sent a 6-character code to ${email}`}
        </Text>
      </div>

      {/* Email Step */}
      {currentStep === 'email' && (
        <EmailStep onSuccess={handleEmailStepSuccess} />
      )}

      {/* Code Step */}
      {currentStep === 'code' && (
        <CodeStep
          email={email}
          onSuccess={handleCodeStepSuccess}
          onBack={handleBackToEmail}
          autoSubmit={shouldAutoSubmit}
        />
      )}

      {/* Trust copy — only on email step */}
      {currentStep === 'email' && (
        <div className="mt-2 flex items-center gap-1.5">
          <LockOutlinedIcon
            aria-hidden="true"
            className="size-[13px] text-content-disabled"
          />
          <Text variant="caption" color="disabled">
            Trusted by professional sub-contractors across the UK
          </Text>
        </div>
      )}

      {/* Social footer */}
      <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-border-default pt-6">
        <Text variant="caption" color="disabled">
          © {new Date().getFullYear()} Billda Ltd.
        </Text>
        <div className="flex items-center gap-4">
          {socialLinks.map(({ href, label, Icon }) => (
            <Link
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={label}
              underline="none"
              className="flex items-center text-content-disabled transition-colors duration-150 hover:text-content-secondary"
            >
              <Icon />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MagicCodeLoginForm;
